import { Inject, Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LoadersService } from '../utils/loaders/loaders.service';
import { ActionTypeEnum } from '../models/cartDetails/actionType.model';
import { CommonInfoService } from './common-info.service';
import { GroupMembershipTypeEnum } from '../models/cartDetails/groupMembershipType.model';
import { LoaderActions } from '../utils/loaders/loader-actions.enum';
import { MaxOptionalMembersPipe, OptionalMembersPipe } from '../utils/pipes/optional-member.pipe';
import SlotStateService from './slot-state.service';
import { DataLayerService } from '../customer-actions/data-layer.service';
import { CartDetailsSourceIdsEnum } from '../models/cartDetails/data-source-ids.enum';
import { NumberDeclensionFormatPipe } from '../utils/pipes/number-declension-format.pipe';
import { CommonModalService } from '../utils/modals/common-modal.service';
import {IBundle, ICartDetails} from '../interfaces/cart-details.interface';
import { WcagService } from '../customer-actions/wcag.service';
import {
  NotificationProviderService,
  NotificationProviderTypeEnum
} from '../external-interfaces/notification-provider.service';
import { CommonModalTemplatesEnum } from '../utils/modals/common-modal-templates.enum';

class IDataModel {
  showLoader = false;
  showSection = false;
  content: IDataModelContent = new IDataModelContent();
  additionalMemberCost = 0; // for BOHO offer only
}

class IDataModelContent {
  header: SafeHtml = '';
  description: SafeHtml = '';
}

const SCROLL_ANIMATION_DURATION = 500;
const SHOW_ANIMATION_DURATION = 750;

@Injectable()
export class OptionalMemberService {
  public model: IDataModel = new IDataModel();

  private isRemoved = false;
  private isAdding = false;
  private optionalMembersPipe: OptionalMembersPipe = new OptionalMembersPipe();
  private maxOptionalMembersPipe: MaxOptionalMembersPipe = new MaxOptionalMembersPipe();
  private numberDescription: NumberDeclensionFormatPipe = new NumberDeclensionFormatPipe();

  constructor(
    @Inject('Endpoint') private endpoint: any,
    private notificationProvider: NotificationProviderService,
    private loadersService: LoadersService,
    private commonInfo: CommonInfoService,
    private slotStateService: SlotStateService,
    private domSanitizer: DomSanitizer,
    private dataLayerService: DataLayerService,
    private commonModalService: CommonModalService,
    private wcagService: WcagService
  ) {
    this.initLoader();

    this.notificationProvider.subscribe(NotificationProviderTypeEnum.CART_DETAILS, (cartDetailsResponse: ICartDetails) => {
      this.updateSectionContent(cartDetailsResponse);
      this.showSection(cartDetailsResponse);
    });
  }

  private initLoader(): void {
    this.loadersService.registerLoader(this.loadersService.loaderActions.ADDING_OPTIONAL_NUMBER, [this.loadersService.loaderActions.SUMMARY_PRICES_LOADER]).subscribe((context: any) => {
      this.model.showLoader = context.visible;
    });
  }

  public numberDeleteConfirmation(slot: any) {
    let resolve: any = null;
    let reject: any = null;

    const promise = new Promise((_resolve: Function, _reject: Function) => {
      resolve = _resolve;
      reject = _reject;
    });

    const modalData = {
      templateName: CommonModalTemplatesEnum.NUMBER_DELETE_CONFIRMATION,
      confirm: ($event: MouseEvent) => {
        $event.preventDefault();

        this.commonModalService.removeModal(CommonModalTemplatesEnum.NUMBER_DELETE_CONFIRMATION);

        resolve();
      },

      numberDescription: this.numberDescription.transform(slot.ordinal)
    };

    this.commonModalService.setData(modalData);

    return promise;
  }

  private updateSectionContent(cartDetails: ICartDetails): void {
    const actionType = cartDetails.commonProcessData.actionType;
    const group = cartDetails.commonProcessData.group;
    const maxMembers = group.toMax + group.currently;
    const groupTimesUp = cartDetails.commonProcessData.group.timesUp;

    const fillGroup = group.toMin === 0 && (actionType === ActionTypeEnum.CREATE || actionType === ActionTypeEnum.ADD);
    const createMinGroup = group.toMin !== 0 && actionType === ActionTypeEnum.CREATE;
    const addToMinGroupBeforeEndingDate = group.toMin !== 0 && actionType === ActionTypeEnum.ADD && !groupTimesUp;
    const addToMinGroup = group.toMin !== 0 && actionType === ActionTypeEnum.ADD && groupTimesUp;

    let header = '';

    const groupHeader = `Dodaj kolejny numer telefonu. <br>Do pełnej Grupy brakuje <b data-automation-id="add-member-how-many-numbers-left">${this.optionalMembersPipe.transform(group.toMin)}.</b>`;

    if (fillGroup && !cartDetails.nextSim) {
      header = `<span data-automation-id="add-member-complete-group-link">Możesz dodać do Grupy maksymalnie <br><b data-automation-id="add-member-how-many-numbers-left">${this.maxOptionalMembersPipe.transform(maxMembers, true)}</b></span>`;

    } else if (createMinGroup) {
      header = `<span data-automation-id="add-member-new-incomplete-group-link">${groupHeader}</span>`;

    } else if (addToMinGroupBeforeEndingDate) {
      header = `<span data-automation-id="add-member-new-incomplete-group-ending-date-link">${groupHeader}</span>`;

    } else if (addToMinGroup) {
      header = `<span data-automation-id="add-member-existing-incomplete-group-link">${groupHeader}</span>`;
    }

    this.model.content.header = this.domSanitizer.bypassSecurityTrustHtml(header);

    if (this.commonInfo.isBohoOffer) {
      this.model.additionalMemberCost = <number>cartDetails.additionalMemberPrice[this.commonInfo.taxName];
    }
  }

  private showSection(cartDetails: ICartDetails): boolean {
    const actionType = cartDetails.commonProcessData.actionType;
    const group = cartDetails.commonProcessData.group;
    const isSolo = this.commonInfo.isSolo;
    const isPlayBrand = !this.commonInfo.isVirginBrand;
    const isNextSim = this.commonInfo.isNextSim;

    // hide slot when type of it is PLAY brand without nextSim, 'voucher', or user used all allowed slots, or offer is solo.
    const shouldHideSection = isPlayBrand && !isNextSim || actionType === ActionTypeEnum.VOUCHER || group.toMax === 0 || isSolo;
    this.model.showSection = !shouldHideSection;

    return !shouldHideSection;
  }

  private findLastMember(bundles: IBundle[]) {
    const membersOnly = bundles.filter((bundle: IBundle) => {
      return bundle.offer.groupMembership === GroupMembershipTypeEnum.MEMBER;
    });

    return membersOnly[membersOnly.length - 1];
  }

  private handleNewSlot(response: ICartDetails): void {
    const newSlot = this.findLastMember(response.bundles);
    newSlot.newSlotHide = true;

    response._sourceId = CartDetailsSourceIdsEnum.OPTIONAL_MEMBER;
    response._sourceToken = this.dataLayerService.getToken(CartDetailsSourceIdsEnum.OPTIONAL_MEMBER);

    newSlot.newSlotShowFromBottom = true;

    this.notificationProvider.publish(NotificationProviderTypeEnum.CART_DETAILS, response);

    setTimeout(() => {
      newSlot.newSlotShowFromBottom = false;
    }, 1200);
  }

  addNewSlot() {
    if (this.isAdding) {
      return;
    }

    this.wcagService.startQueue('Dodajemy kolejny numer do Twojego koszyka');

    this.isAdding = true;

    this.loadersService.start(LoaderActions.ADDING_OPTIONAL_NUMBER);

    this.dataLayerService.generateToken(CartDetailsSourceIdsEnum.OPTIONAL_MEMBER);

    this.dataLayerService.onAddOptionalSlot();

    this.endpoint('newCart.addOptionalMember')
      .queue()
      .run()
      .then((serverResponse: any) => {
        this.handleNewSlot(serverResponse);
      })
      .finally(() => {
        setTimeout(() => {
          this.loadersService.stop(LoaderActions.ADDING_OPTIONAL_NUMBER);

          this.isAdding = false;
        }, SHOW_ANIMATION_DURATION + SCROLL_ANIMATION_DURATION);

        this.wcagService.startQueue('Dodano nowy numer');
      });
  }

  removeOptionalMember(slot: any) {
    this.wcagService.startQueue('Usuwamy numer z koszyka');

    let resolve: any = null;
    let reject: any = null;

    const promise = new Promise((_resolve: Function, _reject: Function) => {
      resolve = _resolve;
      reject = _reject;
    });

    if (typeof slot === 'undefined' || this.isRemoved) {
      reject(false);

      return promise;
    }

    this.isRemoved = true;

    this.loadersService.start(this.loadersService.loaderActions.SLOT_LOADER + slot.slotNo);

    this.dataLayerService.generateToken(CartDetailsSourceIdsEnum.REMOVE_SLOT);

    this.dataLayerService.onRemoveOptionalSlot(slot.uid);

    this.endpoint('newCart.removeOptionalMember', {
      bundleNo: slot.bundleNumber
    }).queue()
      .run()
      .then((response: ICartDetails) => {
        this.slotStateService.dropTempState(slot.bundleNumber);

        response._sourceId = CartDetailsSourceIdsEnum.REMOVE_SLOT;
        response._sourceToken = this.dataLayerService.getToken(CartDetailsSourceIdsEnum.REMOVE_SLOT);

        this.notificationProvider.publish(NotificationProviderTypeEnum.CART_DETAILS, response);

        resolve();
      })
      .finally(() => {
        this.wcagService.startQueue('Usunięto' + this.numberDescription.transform(slot.ordinal) + ' z koszyka');
        this.loadersService.stop(this.loadersService.loaderActions.SLOT_LOADER + slot.slotNo);

        this.isRemoved = false;
      });

    return promise;
  }
}
